// ---------------------------------------------------------------------------
// When the "connect a Sending Account" wizard has finished, and whether it worked
//
// The mailbox connect is a popup + poll: open Unipile's hosted auth, then poll
// GET /api/connect/unipile-email/status until the flow ends. The status the
// server returns carries two signals — `newAccountsFound` (a mailbox was filed)
// and `completed` (the wizard finished; ALSO true when the person reconnected a
// mailbox Unipile already held, which files nothing new).
//
// The blocker this fixes: the client treated `newAccountsFound > 0` as the ONLY
// success. Reconnecting a mailbox that was left connected at Unipile after its
// Sales Rep was deleted files nothing new, so the poll saw 0, the popup closed,
// and the flow stopped silently — no assignment, no message, "nothing happens".
// These pure predicates let the page end the poll and announce success on a
// reconnect too, and are unit-testable without a DOM or a live popup.
// ---------------------------------------------------------------------------

export interface EmailConnectStatus {
  ok?: boolean;
  /** How many brand-new mailboxes the server just filed. */
  newAccountsFound?: number;
  /** The server considers the wizard finished — filed something, or a reconnect
   *  of a known mailbox that needed nothing filed. NOT the same as "new". */
  completed?: boolean;
}

/**
 * The server reports the connect flow finished: it filed a new mailbox, or the
 * wizard completed with nothing new to file (a reconnect of a mailbox Unipile
 * already held). A failed/!ok status is never "done".
 */
export function serverReportedDone(status: EmailConnectStatus): boolean {
  return status.ok === true && ((status.newAccountsFound ?? 0) > 0 || status.completed === true);
}

/**
 * Should the poll stop this tick? Once the popup is closed the user is finished
 * with the provider either way; otherwise wait for the server to report done.
 */
export function emailConnectFlowEnded(popupClosed: boolean, status: EmailConnectStatus): boolean {
  return popupClosed || serverReportedDone(status);
}

/**
 * After the flow ended and unassigned mailboxes were reconciled onto the rep, is
 * there a connected mailbox worth announcing? A newly filed one or a completed
 * reconnect (serverReportedDone), OR one we just attached — the reconnected
 * mailbox that was sitting unassigned after its Sales Rep was deleted.
 */
export function emailConnectSucceeded(status: EmailConnectStatus, assignedCount: number): boolean {
  return serverReportedDone(status) || assignedCount > 0;
}
